import { useCallback, useEffect, useRef, useState } from 'react';
import {
  startOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  subDays,
  addMonths,
  subMonths,
  isSameMonth,
  setMonth,
  setYear,
} from 'date-fns';
import { isDateDisabled, computeCalendarDropdownPosition } from './calendar-utils';
import type { CalendarView, CalendarDropdownPosition } from './calendar-utils';

export interface UseCalendarPopupOptions {
  open: boolean;
  isMobile: boolean;
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  initialViewDate: Date;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
  /** Minimum dropdown width (defaults to 320). */
  baseMinWidth?: number;
  /** Max desired popup height before capping to the viewport (defaults to CALENDAR_FRAME_HEIGHT). */
  frameHeight?: number;
  /** Ref to the element that should receive focus when the panel opens on mobile (e.g. close button). */
  mobileInitialFocusRef?: React.RefObject<HTMLElement | null>;
  /**
   * Ref to the mobile dialog container, so an outside-press can tell taps INSIDE the
   * mobile calendar from taps outside it. `panelRef` only covers the desktop dropdown
   * (PB-4947).
   */
  mobileDialogRef?: React.RefObject<HTMLElement | null>;
  /**
   * Surfaces that co-own this picker — typically the toolbar/search row(s) the trigger sits in
   * (PB-4947).
   *
   * A press on one of these dismisses the calendar and is then allowed through, so the control
   * the user aimed at still acts: one tap switches between the row's own controls. A press
   * anywhere else dismisses and is swallowed, so no background element activates behind a modal
   * calendar. Omitted or empty, every outside press is swallowed.
   *
   * Takes a LIST because the owning controls are frequently siblings with no shared wrapper.
   * Do not "simplify" this by wrapping them in a `display: contents` div and passing one ref:
   * such an element generates no box, so it is never a hit-test target and `contains()` falls
   * through to an ancestor — which marks the whole page owned and lets every background press
   * act. That was the failure this signature exists to prevent.
   */
  ownedSurfaceRefs?: ReadonlyArray<React.RefObject<HTMLElement | null>>;
}

/**
 * Shared popup behaviour for CalendarPanel and RangeCalendarPanel.
 *
 * Encapsulates:
 * - Desktop dropdown positioning (with flip logic)
 * - Body scroll lock on mobile
 * - Focus management on open and after keyboard navigation
 * - Outside click / external scroll / Escape+Tab close handlers
 * - Month/year view navigation callbacks
 * - Arrow key grid navigation (WAI-ARIA grid pattern)
 *
 * The returned `onDayClickRef` must be set by the consumer to the handler that
 * should run when Enter/Space is pressed on a day cell. This avoids a circular
 * dependency (the handler typically needs `setFocusedDate` from this hook).
 */
export function useCalendarPopup({
  open,
  isMobile,
  anchorRef,
  onClose,
  initialViewDate,
  minDate,
  maxDate,
  filterDate,
  baseMinWidth = 320,
  frameHeight,
  mobileInitialFocusRef,
  mobileDialogRef,
  ownedSurfaceRefs,
}: UseCalendarPopupOptions) {
  const panelRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Consumers assign their handleDayClick to this ref after calling the hook.
  // handleGridKeyDown reads from it, avoiding a circular dependency.
  const onDayClickRef = useRef<(date: Date) => void>(() => {});

  const [viewDate, setViewDate] = useState<Date>(() => initialViewDate);
  const [focusedDate, setFocusedDate] = useState<Date>(() => initialViewDate);
  const [calendarView, setCalendarView] = useState<CalendarView>('days');

  // Ref lets the open-effect read the latest initialViewDate without depending
  // on it — so changes to the prop don't disrupt in-flight user navigation.
  const initialViewDateRef = useRef(initialViewDate);
  useEffect(() => {
    initialViewDateRef.current = initialViewDate;
  }, [initialViewDate]);

  // Reset on open: consumers that keep the popup mounted across open/close
  // cycles would otherwise retain stale viewDate from the first mount.
  useEffect(() => {
    if (open) {
      setViewDate(initialViewDateRef.current);
      setFocusedDate(initialViewDateRef.current);
    }
  }, [open]);

  const [dropdownStyle, setDropdownStyle] = useState<CalendarDropdownPosition>({
    left: 0,
    width: 0,
    maxHeight: 0,
  });

  // ---------- Desktop dropdown positioning ----------
  // Placement is a pure function of the anchor rect + viewport (no element
  // measurement), so it's stable and reflows on resize/zoom to stay on-screen.
  useEffect(() => {
    if (!open || isMobile || !anchorRef?.current) return;
    const position = () => {
      if (!anchorRef.current) return;
      setDropdownStyle(
        computeCalendarDropdownPosition({
          anchorRect: anchorRef.current.getBoundingClientRect(),
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          baseMinWidth,
          frameHeight,
        })
      );
    };
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [open, isMobile, anchorRef, baseMinWidth, frameHeight]);

  // ---------- Body scroll lock on mobile ----------
  useEffect(() => {
    if (!open || !isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, isMobile]);

  // ---------- Focus management on open ----------
  useEffect(() => {
    if (!open) return;
    if (isMobile) {
      mobileInitialFocusRef?.current?.focus();
    } else {
      const frame = requestAnimationFrame(() => {
        gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [open, isMobile, mobileInitialFocusRef]);

  // ---------- Move focus to keyboard-navigated cell ----------
  useEffect(() => {
    if (!open || isMobile || calendarView !== 'days') return;
    const frame = requestAnimationFrame(() => {
      const active = gridRef.current?.querySelector<HTMLElement>('[tabindex="0"]');
      if (active && gridRef.current?.contains(document.activeElement)) {
        active.focus();
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [focusedDate, open, isMobile, calendarView]);

  /**
   * Outside-press dismissal, on BOTH desktop and mobile (PB-4947).
   *
   * ## Invariants:
   * - Listens on `document`, so it is independent of paint order. The mobile backdrop sits
   *   at z-40 while the reserve search row renders at z-49/z-50 on its own compositing
   *   layer, so every control in that row paints ABOVE the backdrop and its `onClick`
   *   never fires. A document listener still sees those taps.
   * - The backdrop keeps its own `onClick` and its z-index. It still blocks what is beneath
   *   it, so a press on an element UNDER the backdrop dismisses the calendar without
   *   activating that element. Letting presses through the backdrop instead would close the
   *   calendar AND action the thing underneath (a garage card would select itself).
   * - `pointerdown`, not `mousedown`: the latter is not emitted for touch input, which is
   *   why mobile was opted out of this effect rather than fixed. Firing before `click` also
   *   means the pressed control still receives its own click, so one tap does one thing.
   * - Three containment guards, each covering a different surface: `anchorRef` (the trigger —
   *   it owns its own toggle), `panelRef` (the desktop dropdown) and `mobileDialogRef` (the
   *   mobile sheet). `panelRef` is NOT attached on mobile, so without the third guard a tap
   *   inside the mobile calendar would dismiss it.
   * - Everything else is DISMISS-ONLY: the press, and the `click` it would produce, are both
   *   swallowed so no background control fires behind an `aria-modal` calendar. The backdrop
   *   cannot enforce this alone — it is a non-portaled sibling at the same z-index as the
   *   reserve garage sheet, which also carries `will-change: transform`, and on a z-index tie
   *   a composited layer paints over its sibling. The sheet therefore sits ABOVE the backdrop
   *   and its cards stayed tappable. Working at the document level is independent of paint
   *   order, so this holds for any such element rather than only the ones we found.
   * - `ownedSurfaceRefs` are the one exception: the row(s) the trigger lives in. A press there
   *   dismisses AND is let through, so switching between the row's controls stays one tap.
   *   Without it those controls would dismiss and do nothing, which is the original bug.
   */
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: Event) => {
      const target = e.target as Node;
      if (anchorRef?.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      if (mobileDialogRef?.current?.contains(target)) return;
      // Co-owned row: dismiss, but let the press through so the control acts.
      if (ownedSurfaceRefs?.some((r) => r.current?.contains(target))) {
        onClose();
        return;
      }
      // `pointerdown` and `click` are separate events, so consuming this one does NOT stop
      // the click that follows — and background controls listen on `onClick`. Swallow one
      // click in the capture phase, before it reaches them.
      // NOT `stopPropagation()` here: the background controls act on `click`, not
      // `pointerdown`, so stopping this one blocks nothing and would also hide the press from
      // unrelated listeners (drag handles, analytics). The click swallow below is what does
      // the work. `preventDefault` stays only to suppress the browser's own synthesized
      // click where it honours it.
      e.preventDefault();
      const disarm = () => {
        document.removeEventListener('click', swallowClick, true);
        document.removeEventListener('pointerup', armedCleanup, true);
        document.removeEventListener('pointercancel', disarm, true);
      };
      const swallowClick = (clickEvent: Event) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        disarm();
      };
      /**
       * Disarms one frame after the press ends.
       *
       * A `click` follows `pointerup` on the same gesture, so the listener must survive past
       * pointerup — but not indefinitely: a press that ends WITHOUT a click (a drag, a scroll,
       * a gesture the browser cancels) would otherwise leave it armed to eat an unrelated
       * later click. rAF is the shortest wait that still spans pointerup → click.
       *
       * NOT `setTimeout(0)` from pointerdown, which was the earlier mistake here: on touch the
       * click lands well after the current task (pointerup, plus any tap delay), so a
       * zero-delay cleanup removed the listener long before the click it was meant to catch.
       */
      const armedCleanup = () => {
        requestAnimationFrame(disarm);
      };
      document.addEventListener('click', swallowClick, true);
      document.addEventListener('pointerup', armedCleanup, true);
      document.addEventListener('pointercancel', disarm, true);
      onClose();
    };
    // Capture phase: the press must be seen before a background control's own listener.
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [open, onClose, anchorRef, mobileDialogRef, ownedSurfaceRefs]);

  // ---------- External scroll close ----------
  useEffect(() => {
    if (!open || isMobile) return;
    const handleScroll = (e: Event) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [open, isMobile, onClose]);

  // ---------- Escape / Tab close ----------
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
      if (e.key === 'Tab' && !isMobile) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isMobile, onClose]);

  // ---------- Month / Year navigation ----------
  const handlePrevMonth = useCallback(() => {
    setViewDate((prev) => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewDate((prev) => addMonths(prev, 1));
  }, []);

  const handleMonthButtonClick = useCallback(() => {
    setCalendarView((prev) => (prev === 'months' ? 'days' : 'months'));
  }, []);

  const handleYearButtonClick = useCallback(() => {
    setCalendarView((prev) => (prev === 'years' ? 'days' : 'years'));
  }, []);

  const handleMonthSelect = useCallback((month: number) => {
    setViewDate((prev) => setMonth(prev, month));
    setCalendarView('days');
  }, []);

  const handleYearSelect = useCallback((year: number) => {
    setViewDate((prev) => setYear(prev, year));
    setCalendarView('days');
  }, []);

  // ---------- Grid keyboard navigation (WAI-ARIA grid pattern) ----------
  const handleGridKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let nextDate: Date | null = null;

      switch (e.key) {
        case 'ArrowLeft':
          nextDate = subDays(focusedDate, 1);
          break;
        case 'ArrowRight':
          nextDate = addDays(focusedDate, 1);
          break;
        case 'ArrowUp':
          nextDate = subDays(focusedDate, 7);
          break;
        case 'ArrowDown':
          nextDate = addDays(focusedDate, 7);
          break;
        case 'Home':
          nextDate = startOfWeek(focusedDate);
          break;
        case 'End':
          nextDate = endOfWeek(focusedDate);
          break;
        case 'PageUp':
          nextDate = subMonths(focusedDate, 1);
          break;
        case 'PageDown':
          nextDate = addMonths(focusedDate, 1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isDateDisabled(focusedDate, minDate, maxDate, filterDate)) {
            onDayClickRef.current(focusedDate);
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      if (nextDate) {
        if (!isSameMonth(nextDate, viewDate)) {
          setViewDate(startOfMonth(nextDate));
        }
        setFocusedDate(nextDate);
      }
    },
    [focusedDate, viewDate, minDate, maxDate, filterDate]
  );

  return {
    viewDate,
    setViewDate,
    focusedDate,
    setFocusedDate,
    calendarView,
    setCalendarView,
    dropdownStyle,
    panelRef,
    gridRef,
    onDayClickRef,
    handlePrevMonth,
    handleNextMonth,
    handleMonthButtonClick,
    handleYearButtonClick,
    handleMonthSelect,
    handleYearSelect,
    handleGridKeyDown,
  };
}
